/**
 * Private groups / leagues — social graph API (Phase 1).
 * Money paths untouched; mutations via Cloud Functions + invite writes.
 */
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../auth/firebaseAuth';
import { hasLockedInPlus } from '../utils/platformFee';
import {
  buildGroupInvitePayload,
  getGroupInviteErrorMessage,
  groupInviteDocId,
} from '../utils/groupInviteErrors';
import { logGroupInviteDebug } from '../utils/groupInviteDebug';

function getFunctionsBaseUrl() {
  const base = process.env.REACT_APP_CLOUD_FUNCTIONS_URL;
  if (base) return base.replace(/\/$/, '');
  const projectId = process.env.REACT_APP_PROJECT_ID;
  if (!projectId) return null;
  const region = process.env.REACT_APP_FUNCTIONS_REGION || 'us-central1';
  if (process.env.REACT_APP_USE_EMULATORS === 'true') {
    const emulatorPort = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5011';
    return `http://127.0.0.1:${emulatorPort}/${projectId}/${region}`;
  }
  return `https://${region}-${projectId}.cloudfunctions.net`;
}

async function getIdToken() {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to use groups.');
  return user.getIdToken();
}

async function postGroupFunction(path, body) {
  const base = getFunctionsBaseUrl();
  if (!base) throw new Error('Cloud Functions URL not configured.');
  const idToken = await getIdToken();
  const res = await fetch(`${base}/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

export function canCreateGroup(profile) {
  return hasLockedInPlus(profile);
}

export async function createGroup({ name, description, sport }) {
  return postGroupFunction('create_group', { name, description, sport });
}

export async function acceptGroupInvite(inviteId) {
  return postGroupFunction('accept_group_invite', { inviteId });
}

export async function declineGroupInvite(inviteId) {
  const ref = doc(db, 'groupInvites', inviteId);
  await updateDoc(ref, { status: 'DECLINED', updatedAt: serverTimestamp() });
}

export async function leaveGroup(groupId) {
  return postGroupFunction('leave_group', { groupId });
}

export async function transferGroupOwnership(groupId, newOwnerId) {
  return postGroupFunction('transfer_group_ownership', { groupId, newOwnerId });
}

export async function sendGroupInvite({
  groupId,
  groupName,
  toUserId,
  fromDisplayName,
}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to invite members.');
  if (!groupId || !toUserId) throw new Error('Invalid invite.');
  if (toUserId === user.uid) throw new Error('You cannot invite yourself.');

  logGroupInviteDebug('invite_start', { groupId });

  try {
    const memberRef = doc(db, 'groups', groupId, 'members', user.uid);
    const memberSnap = await getDoc(memberRef);
    if (!memberSnap.exists()) {
      logGroupInviteDebug('invite_not_member', { groupId });
      throw new Error('Invite not sent — please confirm you\'re allowed to invite members to this league.');
    }

    const inviteId = groupInviteDocId(groupId, toUserId);
    const inviteRef = doc(db, 'groupInvites', inviteId);
    const now = Timestamp.now();
    const payload = buildGroupInvitePayload({
      groupId,
      groupName,
      fromUserId: user.uid,
      fromDisplayName,
      toUserId,
      now,
    });

    await setDoc(inviteRef, payload);
    logGroupInviteDebug('invite_success', { groupId, inviteId });
  } catch (err) {
    if (
      err?.message?.includes('allowed to invite members')
      || err?.message?.includes('pending invite')
      || err?.message?.includes('Sign in to invite')
      || err?.message?.includes('cannot invite yourself')
      || err?.message?.includes('Invalid invite')
    ) {
      throw err;
    }
    logGroupInviteDebug('invite_permission_denied', { groupId, code: err?.code || null });
    throw new Error(getGroupInviteErrorMessage(err));
  }
}

export function subscribeMyGroupMemberships(userId, onData, onError) {
  if (!userId) return () => {};
  const ref = collection(db, 'users', userId, 'groupMemberships');
  return onSnapshot(ref, (snap) => {
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    onData(rows);
  }, onError);
}

export function subscribeIncomingGroupInvites(userId, onData, onError) {
  if (!userId) return () => {};
  const q = query(
    collection(db, 'groupInvites'),
    where('toUserId', '==', userId),
    where('status', '==', 'PENDING'),
  );
  return onSnapshot(q, (snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, onError);
}

export function subscribeGroupMembers(groupId, onData, onError) {
  if (!groupId) return () => {};
  const ref = collection(db, 'groups', groupId, 'members');
  return onSnapshot(ref, (snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, onError);
}

export async function fetchGroup(groupId) {
  const snap = await getDoc(doc(db, 'groups', groupId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export function subscribeGroupActivity(groupId, onData, onError) {
  if (!groupId) return () => {};
  const q = query(
    collection(db, 'groups', groupId, 'activity'),
    orderBy('createdAt', 'desc'),
    limit(40),
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError,
  );
}

export async function fetchGroupLeaderboard(groupId) {
  return postGroupFunction('get_group_leaderboard', { groupId });
}

export function formatGroupActivityLine(event) {
  const name = event?.actorName || 'Someone';
  const target = event?.targetName;
  switch (event?.type) {
    case 'lock_posted':
      return `${name} posted a lock`;
    case 'lock_accepted':
      return target ? `${name} accepted ${target}'s lock` : `${name} accepted a lock`;
    case 'member_defeated':
      return target ? `${name} defeated ${target}` : `${name} won a matchup`;
    case 'weekly_champion':
      return `${name} is leading the week`;
    case 'member_joined':
      return target ? `${name} joined` : `${name} joined the group`;
    case 'member_left':
      return `${name} left the group`;
    case 'group_created':
      return `${name} created the group`;
    case 'ownership_transferred':
      return target ? `${name} transferred ownership to ${target}` : `${name} transferred ownership`;
    default:
      return `${name} — ${(event?.type || 'activity').replace(/_/g, ' ')}`;
  }
}
